// Minimal, dependency-free allowlist HTML sanitizer.
//
// Live-editable labels (GET /labels/live overrides + the Translatize publish
// webhook) are editor/platform-controllable and win over the bundled JSON. A
// few label strings legitimately contain formatting HTML (e.g. the signup
// "Agree to <a href>Terms</a>" checkbox) and are rendered with
// dangerouslySetInnerHTML. Without sanitization an editor could set a label to
// `<img src=x onerror=...>` and get arbitrary JS execution in every visitor's
// browser (including unauthenticated login/signup pages). Escaping outright
// would break the legitimate links, so we parse and strip everything outside a
// small allowlist instead.

const ALLOWED_TAGS = new Set([
    'A', 'B', 'STRONG', 'I', 'EM', 'U', 'BR', 'SPAN', 'P', 'DIV', 'UL', 'OL', 'LI', 'SMALL'
]);

const ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'class']);

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const sanitizeElement = (element: Element): void => {
    Array.from(element.children).forEach((child) => {
        if (!ALLOWED_TAGS.has(child.tagName)) {
            child.remove();
            return;
        }

        Array.from(child.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();

            if (!ALLOWED_ATTRS.has(name)) {
                child.removeAttribute(attr.name);
                return;
            }

            if (name === 'href') {
                const value = attr.value.trim().toLowerCase();
                if (value.startsWith('javascript:') || value.startsWith('data:') || value.startsWith('vbscript:')) {
                    child.removeAttribute(attr.name);
                }
            }
        });

        sanitizeElement(child);
    });
};

export const sanitizeHtml = (dirty: string): string => {
    if (!dirty) {
        return '';
    }

    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        // No DOM available (SSR/tests) — fall back to full escaping.
        return escapeHtml(dirty);
    }

    // text/html parsing does not execute scripts or load resources.
    const doc = new DOMParser().parseFromString(dirty, 'text/html');
    sanitizeElement(doc.body);
    return doc.body.innerHTML;
};
